import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Alert, InputGroup } from "react-bootstrap";
import {
  verifyBeforeUpdateEmail, updatePassword,
  EmailAuthProvider, reauthenticateWithCredential
} from "firebase/auth";
import { doc, getDoc, updateDoc, query, collection, where, getDocs } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { auth, database, storage } from "../Firebase";
import { useNavigate } from "react-router-dom";
import { LuEye, LuEyeOff } from 'react-icons/lu';

// ── Sanitisation ──────────────────────────────────────────────────────────────
const sanitize = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str.trim()
    .replace(/</g,  "&lt;")
    .replace(/>/g,  "&gt;")
    .replace(/"/g,  "&quot;")
    .replace(/'/g,  "&#x27;")
    .replace(/\//g, "&#x2F;");
};

// ── Validations ───────────────────────────────────────────────────────────────
const VALIDATIONS = {
  pseudo:         { regex: /^[a-zA-Z0-9_\-]{3,20}$/,            message: "3-20 caractères, lettres, chiffres, _ ou -" },
  telephone:      { regex: /^(06|07)[0-9]{8}$/,                  message: "Format : 06 ou 07 suivi de 8 chiffres" },
  immatriculation:{ regex: /^[A-Za-z]{2}-[0-9]{3}-[A-Za-z]{2}$/, message: "Format attendu : AB-123-CD" },
  password:       {
    regex: /^(?=.*\d)(?=.*[A-Z])(?=.*[!@#$%^&*\\.]).{8,}$/,
    message: "8 caractères minimum dont 1 majuscule, 1 chiffre et 1 caractère spécial"
  },
};

// ── Composant mot de passe ────────────────────────────────────────────────────
const PasswordInput = ({ label, name, value, onChange, placeholder, required, feedbackMessage, isInvalid }) => {
  const [showPassword, setShowPassword] = useState(false);
  return (
    <Form.Group className="mb-3">
      <Form.Label>{label}</Form.Label>
      <InputGroup hasValidation>
        <Form.Control
          type={showPassword ? "text" : "password"}
          name={name} value={value} onChange={onChange}
          placeholder={placeholder} required={required}
          isInvalid={isInvalid}
          autoComplete="new-password"
        />
        <InputGroup.Text onClick={() => setShowPassword(p => !p)} style={{ cursor: 'pointer' }}>
          {showPassword ? <LuEyeOff size={20} /> : <LuEye size={20} />}
        </InputGroup.Text>
        <Form.Control.Feedback type="invalid">{feedbackMessage}</Form.Control.Feedback>
      </InputGroup>
      {name === 'password' && (
        <Form.Text className="text-muted">
          8 caractères minimum (1 majuscule, 1 chiffre, 1 caractère spécial : !@#$%^&*.)
        </Form.Text>
      )}
    </Form.Group>
  );
};

export default function ChangeProfil() {
  const navigate                            = useNavigate();
  const [validated, setValidated]           = useState(false);
  const [showSuccess, setShowSuccess]       = useState(false);
  const [errorMessage, setErrorMessage]     = useState("");
  const [isSubmitting, setIsSubmitting]     = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [fieldErrors, setFieldErrors]       = useState({});
  const [userData, setUserData] = useState({
    pseudo: "", dateNaissance: "", telephone: "",
    password: "", confirmPassword: "",
    typeVehicule: "", marqueVehicule: "", immatriculation: "",
    photoProfil: null, photoProfilURL: "",
  });

  const vehiculeTypes   = ["Deux roues","Berline","Break","Citadine","SUV","Monospace","Coupé","Cabriolet","Camion","Utilitaire"];
  const marqueVehicules = ['Abarth','Acura','Alfa Romeo','Alpina','Aston Martin','Audi','Bentley','BMW','Bugatti','Buick','Cadillac','Chevrolet','Chrysler','Citroën','Dacia','Daewoo','Daihatsu','Dodge','DS','Ducati','Ferrari','Fiat','Ford','Honda','Hyundai','Infiniti','Isuzu','Jaguar','Jeep','Kia','Lada','Lamborghini','Lancia','Land Rover','Lexus','Maserati','Mazda','McLaren','Mercedes-Benz','MG','Mini','Mitsubishi','Morgan','Nissan','Opel','Peugeot','Porsche','Renault','Rover','Saab','SEAT','Skoda','Smart','SsangYong','Subaru','Suzuki','Tesla','Toyota','Vauxhall','Volkswagen','Volvo','Wuling','Zenvo'];

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const user = auth.currentUser;
        if (!user) { navigate('/login'); return; }
        const userSnap = await getDoc(doc(database, "users", user.uid));
        if (userSnap.exists()) {
          const data = userSnap.data();
          setUserData({
            pseudo:          data.pseudo || "",
            dateNaissance:   data.dateNaissance || "",
            telephone:       data.telephone || "",
            password:        "",
            confirmPassword: "",
            typeVehicule:    data.typeVehicule || "",
            marqueVehicule:  data.marqueVehicule || "",
            immatriculation: data.immatriculation || "",
            photoProfil:     null,
            photoProfilURL:  data.photoProfil || "",
          });
        } else {
          setErrorMessage("Aucune donnée utilisateur trouvée");
        }
      } catch (error) {
        setErrorMessage("Erreur lors de la récupération des données");
      }
    };
    fetchUserData();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    const newValue = type === 'file' ? files[0] : value;
    setUserData(prev => ({ ...prev, [name]: newValue }));
    if (VALIDATIONS[name] && type !== 'file') {
      const err = VALIDATIONS[name].regex.test(value) ? null : VALIDATIONS[name].message;
      setFieldErrors(prev => ({ ...prev, [name]: err }));
    }
  };

  // ── Validation image ──────────────────────────────────────────────────────
  const validateImage = (file) => {
    if (!file) return null;
    if (!['image/jpeg','image/png','image/webp','image/gif'].includes(file.type))
      return "Format non autorisé. Utilisez JPG, PNG, WebP ou GIF.";
    if (file.size > 5 * 1024 * 1024)
      return "L'image ne doit pas dépasser 5 Mo.";
    return null;
  };

  // ── Validation âge minimum ────────────────────────────────────────────────
  const validateAge = (dateNaissance) => {
    if (!dateNaissance) return "Date de naissance requise";
    const birth = new Date(dateNaissance);
    const today = new Date();
    const age   = today.getFullYear() - birth.getFullYear()
      - (today < new Date(today.getFullYear(), birth.getMonth(), birth.getDate()) ? 1 : 0);
    return age < 16 ? "Vous devez avoir au moins 16 ans." : null;
  };

  const validateAll = () => {
    const errors = {};
    ['pseudo','telephone','immatriculation'].forEach(field => {
      if (!VALIDATIONS[field].regex.test(userData[field]))
        errors[field] = VALIDATIONS[field].message;
    });
    const ageErr = validateAge(userData.dateNaissance);
    if (ageErr) errors.dateNaissance = ageErr;
    if (userData.password) {
      if (!VALIDATIONS.password.regex.test(userData.password))
        errors.password = VALIDATIONS.password.message;
      if (userData.password !== userData.confirmPassword)
        errors.confirmPassword = "Les mots de passe ne correspondent pas";
    }
    const imgErr = validateImage(userData.photoProfil);
    if (imgErr) errors.photoProfil = imgErr;
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const uploadProfilePicture = async (file, userId) => {
    if (!file) return userData.photoProfilURL;
    const photoRef = ref(storage, `profilePics/${userId}`);
    const snapshot = await uploadBytes(photoRef, file);
    return await getDownloadURL(snapshot.ref);
  };

  const reauthenticateUser = async () => {
    if (!currentPassword) throw new Error("Veuillez entrer votre mot de passe actuel");
    const user       = auth.currentUser;
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const form = e.currentTarget;
    if (!form.checkValidity() || !validateAll()) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    // Demande le mot de passe actuel si changement de password
    if (userData.password && !currentPassword) {
      setShowPasswordFields(true);
      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Utilisateur non connecté");

      // Réauthentification si changement de mot de passe
      if (userData.password && currentPassword) {
        await reauthenticateUser();
      }

      const photoURL = await uploadProfilePicture(userData.photoProfil, user.uid);

      // Changement de mot de passe
      if (userData.password) {
        await updatePassword(user, userData.password);
      }

      // ── Whitelist stricte Firestore ───────────────────────────────────────
      // JAMAIS : role, suspended, abonnement, points, emailVerifie, parrain
      const updatedData = {
        pseudo:          sanitize(userData.pseudo),
        dateNaissance:   userData.dateNaissance,
        telephone:       userData.telephone,
        typeVehicule:    userData.typeVehicule,
        marqueVehicule:  userData.marqueVehicule,
        immatriculation: userData.immatriculation.toUpperCase(),
        photoProfil:     photoURL,
        updatedAt:       new Date().toISOString(),
      };

      await updateDoc(doc(database, "users", user.uid), updatedData);

      setShowSuccess(true);
      setShowPasswordFields(false);
      setCurrentPassword("");
      setUserData(prev => ({ ...prev, password: "", confirmPassword: "" }));
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error("Erreur mise à jour:", error);
      if (error.code === 'auth/requires-recent-login') {
        setShowPasswordFields(true);
        setErrorMessage("Pour des raisons de sécurité, veuillez confirmer votre mot de passe actuel.");
      } else if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        setErrorMessage("Mot de passe actuel incorrect.");
      } else {
        setErrorMessage("Une erreur est survenue. Réessayez.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container className="py-5">
      <h2 className="text-center mb-4">Modifier mon profil</h2>

      {showSuccess && <Alert variant="success" className="mb-4">Profil mis à jour avec succès !</Alert>}
      {errorMessage && <Alert variant="danger" className="mb-4">{errorMessage}</Alert>}

      {/* Confirmation mot de passe actuel */}
      {showPasswordFields && (
        <Alert variant="info" className="mb-4">
          <div className="mb-3" style={{ fontWeight: 600 }}>
            🔐 Confirmez votre mot de passe actuel pour modifier votre mot de passe
          </div>
          <Form.Group>
            <Form.Label>Mot de passe actuel</Form.Label>
            <Form.Control
              type="password" value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
              required autoComplete="current-password"
            />
          </Form.Group>
          <div className="mt-3 d-flex gap-2">
            <Button variant="primary" size="sm" onClick={handleSubmit}>
              Confirmer
            </Button>
            <Button variant="outline-secondary" size="sm"
              onClick={() => { setShowPasswordFields(false); setCurrentPassword(""); }}>
              Annuler
            </Button>
          </div>
        </Alert>
      )}

      <Form noValidate validated={validated} onSubmit={handleSubmit}>

        {/* Pseudo */}
        <Form.Group className="mb-3">
          <Form.Label>Pseudo</Form.Label>
          <Form.Control type="text" name="pseudo" value={userData.pseudo}
            onChange={handleChange} required
            isInvalid={!!fieldErrors.pseudo}
            pattern="[a-zA-Z0-9_\-]{3,20}" />
          <Form.Control.Feedback type="invalid">
            {fieldErrors.pseudo || "Veuillez choisir un pseudo"}
          </Form.Control.Feedback>
        </Form.Group>

        {/* Date de naissance */}
        <Form.Group className="mb-3">
          <Form.Label>Date de naissance</Form.Label>
          <Form.Control type="date" name="dateNaissance" value={userData.dateNaissance}
            onChange={handleChange} required
            isInvalid={!!fieldErrors.dateNaissance}
            max={new Date(new Date().setFullYear(new Date().getFullYear() - 16))
              .toISOString().split('T')[0]} />
          <Form.Control.Feedback type="invalid">
            {fieldErrors.dateNaissance || "Vous devez avoir au moins 16 ans"}
          </Form.Control.Feedback>
        </Form.Group>

        {/* Téléphone */}
        <Form.Group className="mb-3">
          <Form.Label>Téléphone</Form.Label>
          <Form.Control type="tel" name="telephone" value={userData.telephone}
            onChange={handleChange} required
            isInvalid={!!fieldErrors.telephone}
            pattern="(06|07)[0-9]{8}" inputMode="numeric" maxLength={10} />
          <Form.Control.Feedback type="invalid">
            {fieldErrors.telephone || "Format : 06/07 suivi de 8 chiffres"}
          </Form.Control.Feedback>
          <Form.Text muted>Commence par 06 ou 07, 10 chiffres au total</Form.Text>
        </Form.Group>

        {/* Nouveau mot de passe (optionnel) */}
        <PasswordInput
          label="Nouveau mot de passe (laisser vide pour ne pas changer)"
          name="password" value={userData.password} onChange={handleChange}
          placeholder="************"
          feedbackMessage={fieldErrors.password || VALIDATIONS.password.message}
          isInvalid={!!fieldErrors.password}
        />
        <PasswordInput
          label="Confirmer le nouveau mot de passe"
          name="confirmPassword" value={userData.confirmPassword} onChange={handleChange}
          placeholder="************"
          feedbackMessage={fieldErrors.confirmPassword || "Les mots de passe ne correspondent pas"}
          isInvalid={!!fieldErrors.confirmPassword}
        />

        {/* Type véhicule */}
        <Form.Group className="mb-3">
          <Form.Label>Type de véhicule</Form.Label>
          <Form.Select name="typeVehicule" value={userData.typeVehicule}
            onChange={handleChange} required>
            <option value="">Sélectionnez un type</option>
            {vehiculeTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </Form.Select>
          <Form.Control.Feedback type="invalid">Veuillez sélectionner un type</Form.Control.Feedback>
        </Form.Group>

        {/* Marque véhicule */}
        <Form.Group className="mb-3">
          <Form.Label>Marque du véhicule</Form.Label>
          <Form.Select name="marqueVehicule" value={userData.marqueVehicule}
            onChange={handleChange} required>
            <option value="">Sélectionnez une marque</option>
            {marqueVehicules.map(m => <option key={m} value={m}>{m}</option>)}
          </Form.Select>
          <Form.Control.Feedback type="invalid">Veuillez sélectionner une marque</Form.Control.Feedback>
        </Form.Group>

        {/* Immatriculation */}
        <Form.Group className="mb-3">
          <Form.Label>Immatriculation</Form.Label>
          <Form.Control type="text" name="immatriculation"
            value={userData.immatriculation} onChange={handleChange}
            required isInvalid={!!fieldErrors.immatriculation}
            pattern="[A-Za-z]{2}-[0-9]{3}-[A-Za-z]{2}"
            maxLength={9} placeholder="AB-123-CD" />
          <Form.Control.Feedback type="invalid">
            {fieldErrors.immatriculation || "Format : AB-123-CD"}
          </Form.Control.Feedback>
          <Form.Text muted>2 lettres - 3 chiffres - 2 lettres (ex: AB-123-CD)</Form.Text>
        </Form.Group>

        {/* Photo de profil */}
        <Form.Group className="mb-4">
          <Form.Label>Photo de profil</Form.Label>
          {userData.photoProfilURL && (
            <div className="mb-2">
              <img src={userData.photoProfilURL} alt="profil"
                style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '50%' }} />
            </div>
          )}
          <Form.Control type="file" name="photoProfil" onChange={handleChange}
            accept="image/jpeg,image/png,image/webp,image/gif"
            isInvalid={!!fieldErrors.photoProfil} />
          <Form.Control.Feedback type="invalid">{fieldErrors.photoProfil}</Form.Control.Feedback>
          <Form.Text muted>JPG, PNG, WebP ou GIF — 5 Mo maximum</Form.Text>
        </Form.Group>

        <Button type="submit" variant="primary" className="w-100" disabled={isSubmitting}>
          {isSubmitting ? "Mise à jour en cours..." : "Mettre à jour mon profil"}
        </Button>
      </Form>
    </Container>
  );
}